import {
  addBreadcrumb,
  logger,
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  init as sentryInit,
  withErrorBoundary as sentryWithErrorBoundary,
  setContext,
  setTag,
} from "@sentry/react";

import { ENV, isLocal } from "./config/env";
import { instantiatedSegment } from "./segment";

export const initSentry = () => {
  if (!isLocal) {
    sentryInit({
      dsn: process.env.EXPO_PUBLIC_SENTRY_SERVER_DSN,
      environment: ENV,
      enabled: true,
      enableLogs: true,
      sendDefaultPii: true,
      release: process.env.EXPO_PUBLIC_SENTRY_RELEASE,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 1.0,
      // Drop errors whose originating script is the Daily video SDK's own
      // bundles (loaded from Daily's CDN, e.g. c.daily.co/call-machine/...).
      // These are third-party SDK internals we don't own and can't act on.
      // Note: this only catches errors that carry a stack trace pointing at a
      // Daily URL; stackless unhandled rejections (see the enumerateDevices
      // filter in beforeSend) can't be attributed this way.
      denyUrls: [/daily\.co/, /dailywebrtc\.com/, /dailywebrtc\.net/],
      ignoreErrors: [
        // Browser extension internal messaging (e.g. password managers like
        // LastPass, Bitwarden, 1Password) that leaks into onunhandledrejection
        /Object Not Found Matching Id/,
        // Safari/iOS autoplay policy rejecting media playback or clipboard
        // access (e.g. the Daily.js SDK re-calling video.play() when the tab
        // is backgrounded) surfaces as an unhandled promise rejection.
        /The request is not allowed by the user agent or the platform in the current context/,
        // Firefox for iOS content scripts referencing their `__firefox__`
        // global. Not our code.
        /Can't find variable: __firefox__/,
        /window\.__firefox__/,
        // Injected third-party script (browser or extension) referencing its
        // own `swbrowser` global, which our bundle never defines. Matches the
        // bare identifier to cover the Chromium ("swbrowser is not defined")
        // and Safari ("Can't find variable: swbrowser") wordings.
        /\bswbrowser\b/,
        // DuckDuckGo content scripts referencing an unavailable internal feature.
        /feature named `pageContext` was not found/,
        // DuckDuckGo's injected content-scope-scripts messaging transport
        // rejects a webkit message whose origin it can't validate, throwing
        // `Error: invalid origin` as an unhandled rejection. Not our code.
        /invalid origin/,
        // Generic network failures for fetch requests (offline, connection
        // reset, DNS, CORS, request aborted on navigation, ad/content
        // blockers). Surfaces as an unhandled rejection with no stack and is
        // not actionable. Covers the cross-browser message variants.
        /Failed to fetch/,
        /NetworkError when attempting to fetch resource/,
        /Load failed/,
        // Browser/OS-initiated Picture-in-Picture race inside the Daily video
        // SDK: DailyVideo re-calls video.play() on visibility/PiP events, which
        // the browser rejects while a PiP request is in flight. Benign and
        // transient; playback resumes on its own.
        /The video element is processing a Picture-in-Picture request/,
        // Segment's Bing Ads integration calls `new window.UET(...)` after its
        // onload fires, but the Microsoft UET tag (bat.bing.com/bat.js) that
        // defines `window.UET` is frequently blocked by ad/tracking blockers,
        // leaving it undefined. Third-party and not actionable.
        /window\.UET is not a constructor/,
        // Google Maps JS API failing to dynamically load a sub-library (e.g.
        // "marker" for AdvancedMarker in PharmacySelect) via importLibrary().
        // Thrown from Google's own main.js as an unhandled rejection when the
        // network request for the library fails (offline, flaky connection,
        // ad/content blockers). Transient and third-party, not actionable.
        /Could not load "[^"]+"/,
        // In-app browsers (e.g. Facebook, Instagram) inject a `sendDataToNative`
        // bridge that references `window.webkit.messageHandlers` to talk to the
        // native host. That object only exists inside a real WKWebView, so the
        // injected script throws when the page is opened outside one. Not our
        // code; covers the Safari/WebKit and Chromium message variants.
        /window\.webkit\.messageHandlers/,
        /reading 'messageHandlers'/,
        // XBrowser (Android) injects page scripts that call its `xbrowser`
        // JS bridge global, which isn't always defined when they run. Not
        // our code.
        /xbrowser is not defined/,
      ],
      beforeSend: (event) => {
        // Chrome (esp. Android/mobile) rejecting a device enumeration with a
        // native `AbortError: enumerateDevices() failed.` DOMException. The
        // Daily.js SDK's internal devicechange monitor calls
        // navigator.mediaDevices.enumerateDevices() without a catch, so the
        // rejection surfaces as an unhandled rejection with no stack and is not
        // actionable on our side. Match only *unhandled* rejections so the
        // device errors VideoPreview intentionally reports via
        // captureException() are still tracked.
        const isUnhandledEnumerateDevices = event.exception?.values?.some(
          (value) =>
            value.mechanism?.handled === false &&
            /enumerateDevices\(\) failed/.test(value.value ?? ""),
        );
        if (isUnhandledEnumerateDevices) {
          return null;
        }
        return event;
      },
      beforeSendTransaction: (event, hint) => {
        if (event.measurements && instantiatedSegment) {
          instantiatedSegment.track("superclinical-__page-load-metrics", {
            event_context: {
              transaction: event.transaction,
              time_to_first_byte: event.measurements.ttfb?.value,
              first_paint: event.measurements.fp?.value,
              first_contentful_paint: event.measurements.fcp?.value,
              largest_contentful_paint: event.measurements.lcp?.value,
              connection_round_trip_time:
                event.measurements["connection.rtt"]?.value,
            },
          });
        }
        return event;
      },
    });
  }
};

let captureMessage = sentryCaptureMessage;
let captureException = sentryCaptureException;
if (isLocal) {
  captureMessage = console.log as never;
  captureException = console.error as never;
}
export const withErrorBoundary = sentryWithErrorBoundary;

export {
  addBreadcrumb,
  captureException,
  captureMessage,
  logger,
  setContext,
  setTag,
};
